import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Spinner } from '@/components/ui/spinner';

export interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  isLoading?: boolean;
  /** Override the input's aria-autocomplete (defaults to 'list') */
  ariaAutocomplete?: React.AriaAttributes['aria-autocomplete'];
  /** Override the input's data-testid (defaults to 'search-bar') */
  dataTestId?: string;
  /** Override the icon's data-testid (defaults to 'search-bar-icon') */
  iconTestId?: string;
}

const sizeClasses = {
  xs: 'px-10 py-1 text-xs',
  sm: 'px-10 py-2 text-sm',
  md: 'px-10 py-4 text-base',
  lg: 'px-10 py-6 text-base',
  xl: 'px-10 py-8 text-lg',
};

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      placeholder = 'Search',
      value = '',
      onChange,
      onKeyDown,
      className,
      size = 'md',
      disabled = false,
      isLoading = false,
      ariaAutocomplete = 'list',
      dataTestId = 'search-bar',
      iconTestId = 'search-bar-icon',
    },
    ref
  ) => {
    const handleClear = () => {
      onChange?.('');
    };

    return (
      <div
        className={twMerge('relative w-full', className)}
        data-tutorial="search-bar"
      >
        <div className="absolute ml-3 flex h-full items-center justify-center">
          {isLoading ? (
            <Spinner size="md" tone="muted" data-testid="search-bar-spinner" />
          ) : (
            <MagnifyingGlassIcon
              className="text-chariot-text-secondary size-5"
              data-testid={iconTestId}
            />
          )}
        </div>
        <input
          type="text"
          ref={ref}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          role="searchbox"
          aria-label={placeholder}
          aria-autocomplete={ariaAutocomplete}
          className={twMerge(
            'bg-chariot-transparent text-chariot-text-primary w-full rounded-md p-3 pr-10 pl-10 text-base',
            'border-0',
            'placeholder:text-chariot-text-secondary',
            disabled && 'cursor-not-allowed opacity-75',
            sizeClasses[size]
          )}
          data-testid={dataTestId}
        />
        {value && (
          <button
            onClick={handleClear}
            className="absolute top-0 right-3 flex h-full items-center justify-center"
            aria-label="Clear search"
          >
            <XMarkIcon className="text-chariot-text-primary hover:text-chariot-text-primary size-5 transition-colors" />
          </button>
        )}
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
